/**
 * 源批次解析与三种输出。和 backend/internal/fusion 共用同一份
 * tests/fixtures/fusion-cases.json，改行为要两边一起改。
 */

export const FUSION_MESSAGES: Record<string, string> = {
  OK: '解析成功。',
  E_MULTI_DELIMITER: '只能保留一个单独成行的 ~、/ 或 @ 作为分界线。',
  E_MULTI_BLANK: '检测到多处空行，请只保留一处空行作为分界线，或改用 ~、/、@ 分隔。',
  E_EMPTY_SIDE: '分界线的上下两侧都需要填写源。',
  E_COUNT_MISMATCH: '分界线上下两侧的源数量相同才可以生成。',
  E_ODD_LINES: '没有分界线时会对半切分，当前行数是奇数。请添加分界线或补成偶数行。',
  E_NO_SEPARATOR: '请至少填写两行源，并用单独一行的 ~、/、@ 或一处空行分隔上下两组。',
  E_CABINET_MISSING: '请先填写橱柜编号和橱柜区域。',
}

const DELIMITERS = ['~', '/', '@']

export type FusionBatch = {
  code: string
  upper: string[]
  lower: string[]
  sources: string[]
  autoSplit: boolean
}

export type FusionOutputs = {
  code: string
  prefix: string
  compatible: string
  escapedCompatible: string
}

export function fusionMessage(code: string): string {
  return FUSION_MESSAGES[code] ?? code
}

/** 对齐 JS String.trim：ASCII 空白、NBSP、BOM 和其余 Unicode 空白都裁掉。 */
export function fusionTrim(value: string): string {
  return value.replace(/^[\p{Z}\s\uFEFF]+|[\p{Z}\s\uFEFF]+$/gu, '')
}

/** 域名和机柜字段里的空白全部去掉。CDN 配置遇到空格会直接报错。分隔符行保持原样。 */
function normalizeToken(value: string): string {
  const trimmed = fusionTrim(value)
  if (!trimmed || DELIMITERS.includes(trimmed)) return trimmed
  return trimmed.replace(/[\p{Z}\s\uFEFF]+/gu, '')
}

function splitLines(text: string): string[] {
  return String(text ?? '')
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map((line) => normalizeToken(line))
}

function compact(lines: string[]): string[] {
  return lines.filter((line) => line !== '')
}

function isDelimiter(line: string): boolean {
  return DELIMITERS.includes(line)
}

function sideResult(upper: string[], lower: string[], autoSplit: boolean): FusionBatch {
  if (!upper.length || !lower.length) return { code: 'E_EMPTY_SIDE', upper: [], lower: [], sources: [], autoSplit: false }
  return aligned(upper, lower, upper.concat(lower), autoSplit)
}

/** 按位置配对。某一侧多出来的源单独成组：1. 有域名，2. 留空。 */
function aligned(upper: string[], lower: string[], sources: string[], autoSplit: boolean): FusionBatch {
  const n = Math.max(upper.length, lower.length)
  const up = Array.from({ length: n }, (_, i) => upper[i] ?? '')
  const lo = Array.from({ length: n }, (_, i) => lower[i] ?? '')
  if (lower.length > upper.length) {
    for (let i = upper.length; i < n; i += 1) {
      up[i] = lo[i]
      lo[i] = ''
    }
  }
  return { code: 'OK', upper: up, lower: lo, sources, autoSplit }
}

export function parseBatch(text: string): FusionBatch {
  const lines = splitLines(text)

  const delimiterAt: number[] = []
  lines.forEach((line, index) => {
    if (isDelimiter(line)) delimiterAt.push(index)
  })
  if (delimiterAt.length > 1) return { code: 'E_MULTI_DELIMITER', upper: [], lower: [], sources: [], autoSplit: false }
  if (delimiterAt.length === 1) {
    const at = delimiterAt[0]
    return sideResult(compact(lines.slice(0, at)), compact(lines.slice(at + 1)), false)
  }

  const blankRuns: { start: number; end: number }[] = []
  let i = 0
  while (i < lines.length) {
    if (lines[i] !== '') {
      i += 1
      continue
    }
    const start = i
    while (i < lines.length && lines[i] === '') i += 1
    const hasBefore = compact(lines.slice(0, start)).length > 0
    const hasAfter = compact(lines.slice(i)).length > 0
    if (hasBefore && hasAfter) blankRuns.push({ start, end: i })
  }
  if (blankRuns.length > 1) return { code: 'E_MULTI_BLANK', upper: [], lower: [], sources: [], autoSplit: false }
  if (blankRuns.length === 1) {
    const run = blankRuns[0]
    return sideResult(compact(lines.slice(0, run.start)), compact(lines.slice(run.end)), false)
  }

  const all = compact(lines)
  if (all.length === 0) return { code: 'E_NO_SEPARATOR', upper: [], lower: [], sources: [], autoSplit: false }
  // 没有分界线时对半配对。多出来的最后一条单独成组，2. 留空。
  if (all.length === 1) return { code: 'OK', upper: [all[0]], lower: [''], sources: all, autoSplit: false }
  const odd = all.length % 2 !== 0
  const body = odd ? all.slice(0, -1) : all
  const half = body.length / 2
  const upper = body.slice(0, half)
  const lower = body.slice(half)
  if (odd) {
    upper.push(all[all.length - 1])
    lower.push('')
  }
  return { code: 'OK', upper, lower, sources: all.slice(), autoSplit: true }
}

export function generateOutputs(
  sources: string[],
  cabinet: { cabinetNumber: string; cabinetArea: string },
): FusionOutputs {
  const number = normalizeToken(cabinet.cabinetNumber || '')
  const area = normalizeToken(cabinet.cabinetArea || '')
  if (!number || !area) return { code: 'E_CABINET_MISSING', prefix: '', compatible: '', escapedCompatible: '' }
  const normalized = compact((sources || []).map((source) => normalizeToken(String(source ?? ''))))
  return {
    code: 'OK',
    prefix: normalized.map((s) => area + s).join('\n'),
    compatible: normalized.map((s) => s + '|' + number).join('\n'),
    escapedCompatible: normalized.map((s) => area + s + '|' + number).join('\n'),
  }
}

export function buildPair(upper: string, lower: string): string {
  return '"1.' + normalizeToken(upper) + '\n2.' + normalizeToken(lower) + '"'
}

export function buildComposition(manager: string, upper: string, lower: string): string {
  return fusionTrim(manager) + '\n' + buildPair(upper, lower)
}
